import { useEffect, useState } from "react";
import { CheckCircle, Loader2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import type { BulkSessionAction } from "@/types/cooking-session";

interface BulkActionConfirmBarProps {
  action: BulkSessionAction;
  onUndo?: () => Promise<void>;
  onDismiss: () => void;
}

export function BulkActionConfirmBar({ action, onUndo, onDismiss }: BulkActionConfirmBarProps) {
  const { t } = useLingui();
  const [isUndoing, setIsUndoing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(onDismiss, 8000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const message = (() => {
    switch (action) {
      case "COMPLETED": return t`Sessions marked as completed.`;
      case "PAUSED":    return t`Sessions marked as paused.`;
      case "COOKING":   return t`Sessions started cooking.`;
      case "ON_DECK":   return t`Sessions moved to the deck.`;
      case "DELETE":    return t`Sessions deleted.`;
      default:          return t`Action completed.`;
    }
  })();

  async function handleUndo() {
    if (!onUndo) return;
    setIsUndoing(true);
    try { await onUndo(); } finally { setIsUndoing(false); }
  }

  return (
    <div
      data-testid="bulk-confirm-bar"
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-(--color-action) rounded-[0.625rem] px-4 py-4 shadow-[0px_24px_48px_-12px_rgba(16,24,40,0.18)] animate-in fade-in slide-in-from-bottom-2 duration-200"
    >
      <CheckCircle className="h-5 w-5 shrink-0 text-white/80" />
      <span className="text-base text-white/80 whitespace-nowrap">{message}</span>
      {onUndo && (
        <>
          <div className="w-px h-6 bg-white/20 shrink-0" />
          <Button
            variant="ghost"
            onClick={handleUndo}
            disabled={isUndoing}
            className="h-auto gap-1.5 px-0 text-base font-medium text-white hover:bg-transparent hover:text-white/70 whitespace-nowrap"
          >
            {isUndoing
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : t`Undo`}
          </Button>
        </>
      )}
    </div>
  );
}
